import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { FiPlus, FiArrowLeft, FiEdit, FiSearch, FiList, FiColumns, FiMove } from 'react-icons/fi';

const STATUSES = ['To Do', 'In Progress', 'QA', 'Done'];
const STATUS_COLORS = { 'To Do': '#6b7280', 'In Progress': '#3b82f6', 'QA': '#8b5cf6', 'Done': '#10b981' };
const PRIORITY_COLORS = { Low: '#6b7280', Medium: '#3b82f6', High: '#f59e0b', Critical: '#ef4444' };
const TYPE_ICONS = { Bug: '🐛', Task: '✅', Story: '📖', Epic: '⚡' };

const ProjectBoard = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdminHR = user?.role === 'admin' || user?.role === 'hr';
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [view, setView] = useState('board'); // 'board' or 'list'
  const [filterAssignee, setFilterAssignee] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterEpic, setFilterEpic] = useState('');
  const [search, setSearch] = useState('');
  const [taskForm, setTaskForm] = useState({
    title: '', description: '', type: 'Task', priority: 'Medium', assignee: '',
    dueDate: '', parent: '', storyPoints: '', labels: '',
  });
  const [projects, setProjects] = useState([]);
  const [selectedTasks, setSelectedTasks] = useState(new Set());
  const [bulkMoveTarget, setBulkMoveTarget] = useState('');
  const [bulkMoving, setBulkMoving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [projRes, tasksRes, empRes, allProjRes] = await Promise.all([
        api.get(`/projects/${id}`),
        api.get('/tasks', { params: { project: id, limit: 200 } }),
        api.get('/employees', { params: { limit: 200 } }),
        api.get('/projects'),
      ]);
      setProject(projRes.data);
      setTasks(tasksRes.data.tasks || []);
      setEmployees(empRes.data.employees || []);
      setProjects(allProjRes.data.projects || allProjRes.data || []);
    } catch (err) {
      toast.error('Failed to load project');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const epics = tasks.filter(t => t.type === 'Epic');
  const stories = tasks.filter(t => t.type === 'Story');

  const handleAddTask = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...taskForm, project: id };
      if (taskForm.labels) payload.labels = taskForm.labels.split(',').map(l => l.trim()).filter(Boolean);
      if (taskForm.storyPoints) payload.storyPoints = Number(taskForm.storyPoints);
      if (!taskForm.parent) delete payload.parent;
      await api.post('/tasks', payload);
      toast.success('Task created');
      setShowForm(false);
      setTaskForm({ title: '', description: '', type: 'Task', priority: 'Medium', assignee: '', dueDate: '', parent: '', storyPoints: '', labels: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create task');
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await api.put(`/tasks/${taskId}/status`, { status: newStatus });
      setTasks(prev => prev.map(t => t._id === taskId ? { ...t, status: newStatus } : t));
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const toggleSelect = (taskId) => {
    setSelectedTasks(prev => {
      const next = new Set(prev);
      next.has(taskId) ? next.delete(taskId) : next.add(taskId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedTasks.size === filteredTasksForSelect.length) {
      setSelectedTasks(new Set());
    } else {
      setSelectedTasks(new Set(filteredTasksForSelect.map(t => t._id)));
    }
  };

  const handleBulkMove = async () => {
    if (!bulkMoveTarget) { toast.error('Select a destination project'); return; }
    setBulkMoving(true);
    let moved = 0, failed = 0;
    for (const taskId of selectedTasks) {
      try {
        await api.put(`/tasks/${taskId}/move`, { targetProjectId: bulkMoveTarget });
        moved++;
      } catch {
        failed++;
      }
    }
    setBulkMoving(false);
    setSelectedTasks(new Set());
    setBulkMoveTarget('');
    if (moved > 0) toast.success(`${moved} task${moved > 1 ? 's' : ''} moved`);
    if (failed > 0) toast.error(`${failed} task${failed > 1 ? 's' : ''} failed to move`);
    fetchData();
  };

  const filteredTasks = tasks.filter(t => {
    if (filterAssignee && t.assignee?._id !== filterAssignee) return false;
    if (filterPriority && t.priority !== filterPriority) return false;
    if (filterType && t.type !== filterType) return false;
    if (filterEpic) {
      if (filterEpic === 'none' && t.parent) return false;
      if (filterEpic !== 'none' && (t.parent?._id || t.parent) !== filterEpic && t._id !== filterEpic) return false;
    }
    if (search && !t.title.toLowerCase().includes(search.toLowerCase()) && !t.taskId.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const filteredTasksForSelect = filteredTasks;

  if (loading) return <div className="loading">Loading...</div>;
  if (!project) return <div className="loading">Project not found</div>;

  // ── Board grouping: Epic → Story → Tasks ──────────────────────────────────
  const getParentId = t => t.parent?._id || t.parent || null;

  const buildEpicGroups = () => {
    const usedIds = new Set();
    const groups = [];

    epics.forEach(epic => {
      if (!filteredTasks.find(t => t._id === epic._id)) return;
      usedIds.add(epic._id);

      // Stories directly under this epic
      const epicStories = filteredTasks.filter(t => t.type === 'Story' && getParentId(t) === epic._id);
      epicStories.forEach(s => usedIds.add(s._id));

      const storyGroups = epicStories.map(story => {
        const storyTasks = filteredTasks.filter(t => t.type !== 'Story' && t.type !== 'Epic' && getParentId(t) === story._id);
        storyTasks.forEach(t => usedIds.add(t._id));
        return { story, tasks: storyTasks };
      });

      // Tasks directly under epic (not under a story)
      const directTasks = filteredTasks.filter(t => t.type !== 'Epic' && t.type !== 'Story' && getParentId(t) === epic._id);
      directTasks.forEach(t => usedIds.add(t._id));

      groups.push({ epic, storyGroups, directTasks });
    });

    // Orphan stories (no epic parent) with their tasks
    const orphanStories = filteredTasks.filter(t => t.type === 'Story' && !getParentId(t) && !usedIds.has(t._id));
    orphanStories.forEach(story => {
      usedIds.add(story._id);
      const storyTasks = filteredTasks.filter(t => t.type !== 'Story' && t.type !== 'Epic' && getParentId(t) === story._id);
      storyTasks.forEach(t => usedIds.add(t._id));
      groups.push({ epic: null, storyGroups: [{ story, tasks: storyTasks }], directTasks: [] });
    });

    // Remaining unlinked tasks/bugs
    const unlinked = filteredTasks.filter(t => !usedIds.has(t._id));
    if (unlinked.length > 0) {
      groups.push({ epic: null, storyGroups: [], directTasks: unlinked });
    }

    return groups;
  };

  const epicGroups = buildEpicGroups();

  // All tasks in a group flattened (for column count)
  const allGroupTasks = (group) => [
    ...group.storyGroups.flatMap(sg => [sg.story, ...sg.tasks]),
    ...group.directTasks,
  ];

  const renderCard = (task) => (
    <div key={task._id} className={`kanban-card ${task.type === 'Epic' ? 'epic-card' : ''} ${task.type === 'Story' ? 'story-card' : ''}`}
      onClick={() => navigate(`/projects/${id}/tasks/${task._id}`)}>
      <div className="kanban-card-header">
        <span className="kanban-task-id">{task.taskId}</span>
        <span className={`priority-dot priority-${task.priority.toLowerCase()}`} title={task.priority} />
      </div>
      <div className="kanban-card-title">{task.title}</div>
      <div className="kanban-card-meta">
        <span className="kanban-type-badge">{TYPE_ICONS[task.type]} {task.type}</span>
        {task.storyPoints > 0 && <span className="story-points-badge">{task.storyPoints} SP</span>}
        {task.dueDate && (
          <span className={`kanban-due ${new Date(task.dueDate) < new Date() && task.status !== 'Done' ? 'overdue' : ''}`}>
            {new Date(task.dueDate).toLocaleDateString()}
          </span>
        )}
      </div>
      {task.labels?.length > 0 && (
        <div className="kanban-labels">
          {task.labels.map((l, i) => <span key={i} className="kanban-label">{l}</span>)}
        </div>
      )}
      {(task.type === 'Epic' || task.type === 'Story') && task.childCount > 0 && (
        <div className="epic-progress-bar">
          <div className="epic-progress-fill" style={{ width: `${Math.round((task.childDone / task.childCount) * 100)}%` }} />
          <span className="epic-progress-text">{task.childDone}/{task.childCount}</span>
        </div>
      )}
      <div className="kanban-card-footer">
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {task.assignee ? (
            <span className="kanban-avatar" title={task.assignee.name}>
              {task.assignee.name.charAt(0).toUpperCase()}
            </span>
          ) : <span className="kanban-unassigned">Unassigned</span>}
          {task.parent && <span className="kanban-parent-tag">{task.parent.taskId || ''}</span>}
        </div>
        <div className="kanban-move-btns" onClick={e => e.stopPropagation()}>
          {STATUSES.filter(s => s !== task.status).map(s => (
            <button key={s} className="kanban-move-btn" style={{ borderColor: STATUS_COLORS[s] }}
              title={`Move to ${s}`} onClick={() => handleStatusChange(task._id, s)}>
              {s.split(' ').map(w => w[0]).join('')}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  // Render a story sub-swimlane inside an epic swimlane
  const renderStorySwimlane = (story, storyTasks) => (
    <div key={story._id} className="story-swimlane">
      <div className="story-swimlane-header" onClick={() => navigate(`/projects/${id}/tasks/${story._id}`)}>
        <span>{TYPE_ICONS.Story}</span>
        <span className="kanban-task-id">{story.taskId}</span>
        <strong>{story.title}</strong>
        <span className="epic-swimlane-count">{storyTasks.length} tasks</span>
        <span className={`status-badge`} style={{ background: STATUS_COLORS[story.status] + '22', color: STATUS_COLORS[story.status], fontSize: '0.7rem', padding: '0.1rem 0.4rem' }}>
          {story.status}
        </span>
      </div>
      <div className="kanban-board story-board">
        {STATUSES.map(status => {
          const colTasks = storyTasks.filter(t => t.status === status);
          return (
            <div key={status} className="kanban-column">
              <div className="kanban-column-header" style={{ borderTopColor: STATUS_COLORS[status] }}>
                <span>{status}</span>
                <span className="kanban-count">{colTasks.length}</span>
              </div>
              <div className="kanban-cards">
                {colTasks.map(renderCard)}
                {colTasks.length === 0 && <div className="kanban-empty">—</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // Parent selector label/options based on task type being created
  const parentLabel = taskForm.type === 'Story' ? 'Parent Epic' : 'Parent Story';
  const parentOptions = taskForm.type === 'Story'
    ? epics.map(e => ({ value: e._id, label: `${e.taskId} - ${e.title}` }))
    : stories.map(s => ({ value: s._id, label: `${s.taskId} - ${s.title}` }));

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button className="btn-sm btn-secondary" onClick={() => navigate('/projects')}><FiArrowLeft /></button>
          <div>
            <h1>{project.name}</h1>
            <span className="project-key-badge">{project.key}</span>
            <span style={{ marginLeft: '0.75rem', fontSize: '0.85rem', color: '#6b7280' }}>
              {tasks.length} tasks &middot; {epics.length} epics &middot; {stories.length} stories
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <div className="view-toggle">
            <button className={`view-btn ${view === 'board' ? 'active' : ''}`} onClick={() => { setView('board'); setSelectedTasks(new Set()); }} title="Board"><FiColumns /></button>
            <button className={`view-btn ${view === 'list' ? 'active' : ''}`} onClick={() => setView('list')} title="List"><FiList /></button>
          </div>
          {isAdminHR && (
            <button className="btn-sm btn-warning" onClick={() => navigate(`/projects/${id}/edit`)}><FiEdit /></button>
          )}
          <button className="btn-primary" onClick={() => setShowForm(!showForm)}>
            <FiPlus /> {showForm ? 'Cancel' : 'Add Task'}
          </button>
        </div>
      </div>

      {/* Quick Add Task Form */}
      {showForm && (
        <div className="form-card" style={{ marginBottom: '1rem' }}>
          <h3>New Task</h3>
          <form onSubmit={handleAddTask}>
            <div className="form-grid">
              <div className="form-group">
                <label>Title *</label>
                <input value={taskForm.title} onChange={e => setTaskForm({ ...taskForm, title: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Type</label>
                <select value={taskForm.type} onChange={e => setTaskForm({ ...taskForm, type: e.target.value, parent: '' })}>
                  {['Epic', 'Story', 'Task', 'Bug'].map(t => <option key={t} value={t}>{TYPE_ICONS[t]} {t}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Priority</label>
                <select value={taskForm.priority} onChange={e => setTaskForm({ ...taskForm, priority: e.target.value })}>
                  {['Low', 'Medium', 'High', 'Critical'].map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              {taskForm.type !== 'Epic' && (
                <div className="form-group">
                  <label>{parentLabel}</label>
                  <select value={taskForm.parent} onChange={e => setTaskForm({ ...taskForm, parent: e.target.value })}>
                    <option value="">None</option>
                    {parentOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label>Assignee</label>
                <select value={taskForm.assignee} onChange={e => setTaskForm({ ...taskForm, assignee: e.target.value })}>
                  <option value="">Unassigned</option>
                  {employees.map(emp => <option key={emp._id} value={emp._id}>{emp.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Due Date</label>
                <input type="date" value={taskForm.dueDate} onChange={e => setTaskForm({ ...taskForm, dueDate: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Story Points</label>
                <input type="number" min="0" value={taskForm.storyPoints} onChange={e => setTaskForm({ ...taskForm, storyPoints: e.target.value })} placeholder="0" />
              </div>
              <div className="form-group">
                <label>Labels (comma-separated)</label>
                <input value={taskForm.labels} onChange={e => setTaskForm({ ...taskForm, labels: e.target.value })} placeholder="frontend, api, urgent" />
              </div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label>Description</label>
                <textarea value={taskForm.description} onChange={e => setTaskForm({ ...taskForm, description: e.target.value })} rows={2} />
              </div>
            </div>
            <button type="submit" className="btn-primary" style={{ marginTop: '0.75rem' }}>Create {taskForm.type}</button>
          </form>
        </div>
      )}

      {/* Filters */}
      <div className="filters-row" style={{ marginBottom: '1rem' }}>
        <div className="search-box">
          <FiSearch />
          <input placeholder="Search tasks..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select value={filterEpic} onChange={e => setFilterEpic(e.target.value)}>
          <option value="">All Epics</option>
          <option value="none">No Epic</option>
          {epics.map(e => <option key={e._id} value={e._id}>{e.taskId} - {e.title}</option>)}
        </select>
        <select value={filterAssignee} onChange={e => setFilterAssignee(e.target.value)}>
          <option value="">All Assignees</option>
          {employees.map(emp => <option key={emp._id} value={emp._id}>{emp.name}</option>)}
        </select>
        <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)}>
          <option value="">All Priorities</option>
          {['Low', 'Medium', 'High', 'Critical'].map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="">All Types</option>
          {['Epic', 'Story', 'Task', 'Bug'].map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      {/* Board View */}
      {view === 'board' && (
        <div>
          {epicGroups.map((group, gi) => (
            <div key={gi} className="epic-swimlane">
              {/* Epic header (or No-Epic / orphan story header) */}
              {group.epic ? (
                <div className="epic-swimlane-header" onClick={() => navigate(`/projects/${id}/tasks/${group.epic._id}`)}>
                  <span>{TYPE_ICONS.Epic}</span>
                  <span className="kanban-task-id">{group.epic.taskId}</span>
                  <strong>{group.epic.title}</strong>
                  <span className="epic-swimlane-count">{allGroupTasks(group).length} items</span>
                  {group.epic.childCount > 0 && (
                    <span className="epic-swimlane-progress">
                      {Math.round((group.epic.childDone / group.epic.childCount) * 100)}% done
                    </span>
                  )}
                </div>
              ) : group.storyGroups.length > 0 ? (
                <div className="epic-swimlane-header no-epic">
                  <strong>Stories (No Epic)</strong>
                  <span className="epic-swimlane-count">{allGroupTasks(group).length} items</span>
                </div>
              ) : (
                <div className="epic-swimlane-header no-epic">
                  <strong>Backlog (No Epic / Story)</strong>
                  <span className="epic-swimlane-count">{group.directTasks.length} items</span>
                </div>
              )}

              {/* Story sub-swimlanes */}
              {group.storyGroups.map(sg => renderStorySwimlane(sg.story, sg.tasks))}

              {/* Tasks directly under epic (no story) */}
              {group.directTasks.length > 0 && (
                <div className="kanban-board" style={{ marginTop: group.storyGroups.length > 0 ? '0.5rem' : 0 }}>
                  {STATUSES.map(status => {
                    const colTasks = group.directTasks.filter(t => t.status === status);
                    return (
                      <div key={status} className="kanban-column">
                        <div className="kanban-column-header" style={{ borderTopColor: STATUS_COLORS[status] }}>
                          <span>{status}</span>
                          <span className="kanban-count">{colTasks.length}</span>
                        </div>
                        <div className="kanban-cards">
                          {colTasks.map(renderCard)}
                          {colTasks.length === 0 && <div className="kanban-empty">No tasks</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
          {epicGroups.length === 0 && (
            <div className="kanban-board">
              {STATUSES.map(status => (
                <div key={status} className="kanban-column">
                  <div className="kanban-column-header" style={{ borderTopColor: STATUS_COLORS[status] }}>
                    <span>{status}</span><span className="kanban-count">0</span>
                  </div>
                  <div className="kanban-cards"><div className="kanban-empty">No tasks</div></div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* List View */}
      {view === 'list' && (
        <div>
          {/* Bulk action bar */}
          {selectedTasks.size > 0 && (
            <div className="bulk-action-bar">
              <span className="bulk-count">{selectedTasks.size} task{selectedTasks.size > 1 ? 's' : ''} selected</span>
              <select value={bulkMoveTarget} onChange={e => setBulkMoveTarget(e.target.value)}>
                <option value="">Move to project...</option>
                {projects.filter(p => p._id !== id).map(p => (
                  <option key={p._id} value={p._id}>{p.name} ({p.key})</option>
                ))}
              </select>
              <button className="btn-primary" onClick={handleBulkMove} disabled={!bulkMoveTarget || bulkMoving}>
                <FiMove /> {bulkMoving ? 'Moving...' : 'Move'}
              </button>
              <button className="btn-secondary" onClick={() => setSelectedTasks(new Set())}>Clear</button>
            </div>
          )}
          <table className="table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={filteredTasksForSelect.length > 0 && selectedTasks.size === filteredTasksForSelect.length}
                    onChange={toggleSelectAll}
                    title="Select all"
                  />
                </th>
                <th>Sr. No.</th>
                <th>ID</th>
                <th>Type</th>
                <th>Title</th>
                <th>Status</th>
                <th>Priority</th>
                <th>Assignee</th>
                <th>Parent</th>
                <th>SP</th>
                <th>Due Date</th>
              </tr>
            </thead>
            <tbody>
              {filteredTasksForSelect.map((task, index) => (
                <tr key={task._id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/projects/${id}/tasks/${task._id}`)}>
                  <td onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedTasks.has(task._id)}
                      onChange={() => toggleSelect(task._id)}
                    />
                  </td>
                  <td>{index + 1}</td>
                  <td><span className="kanban-task-id">{task.taskId}</span></td>
                  <td>{TYPE_ICONS[task.type]} {task.type}</td>
                  <td>
                    {task.parent && <span style={{ color: '#9ca3af', fontSize: '0.75rem', marginRight: '4px' }}>↳</span>}
                    <strong>{task.title}</strong>
                    {task.labels?.length > 0 && (
                      <div className="kanban-labels" style={{ marginTop: '2px' }}>
                        {task.labels.map((l, i) => <span key={i} className="kanban-label">{l}</span>)}
                      </div>
                    )}
                  </td>
                  <td>
                    <span className="status-badge" style={{ background: STATUS_COLORS[task.status] + '22', color: STATUS_COLORS[task.status] }}>
                      {task.status}
                    </span>
                  </td>
                  <td><span style={{ color: PRIORITY_COLORS[task.priority], fontWeight: 600 }}>{task.priority}</span></td>
                  <td>{task.assignee?.name || '-'}</td>
                  <td>
                    {task.parent
                      ? <span className="kanban-parent-tag">{task.parent.taskId} {TYPE_ICONS[task.parent.type] || ''}</span>
                      : '-'}
                  </td>
                  <td>{task.storyPoints || '-'}</td>
                  <td className={task.dueDate && new Date(task.dueDate) < new Date() && task.status !== 'Done' ? 'overdue-text' : ''}>
                    {task.dueDate ? new Date(task.dueDate).toLocaleDateString() : '-'}
                  </td>
                </tr>
              ))}
              {filteredTasksForSelect.length === 0 && (
                <tr><td colSpan="11" style={{ textAlign: 'center' }}>No tasks found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ProjectBoard;
